import React, { useState } from "react";
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, X } from "lucide-react";

// Consistent drawer-panel header (icon + title + close) — every tool panel
// across every session dock uses this instead of hand-rolling the same markup.
export function DockPanelHeader({ icon: Icon, title, onClose }) {
  return (
    <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon size={14} color="#00C8FF" />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#E8F0FE" }}>{title}</span>
      </div>
      {onClose && <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#7A93B4" }}><X size={15} /></button>}
    </div>
  );
}

// A single collapsible sub-section within a drawer — used when one tool's
// content is naturally made of several named groups (patient background,
// live vitals, etc.) rather than one flat list.
export function DockAccordionItem({ title, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <button onClick={() => setOpen((o) => !o)}
        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "12px 2px", background: "none", border: "none", cursor: "pointer" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#E8F0FE" }}>{title}</span>
        {open ? <ChevronUp size={13} color="#7A93B4" /> : <ChevronDown size={13} color="#7A93B4" />}
      </button>
      {open && <div style={{ paddingBottom: 14 }}>{children}</div>}
    </div>
  );
}

/**
 * Shared chrome for the collapsible blue icon-dock used on every live
 * session screen (Hirality Meetings, eKYC, Interview, Healthcare): a small
 * round entry point that expands into a rail of themed tool icons, each
 * opening its own drawer. This component owns only the chrome (collapse
 * state, rail, drawer shell, badges) — callers own their own tools list,
 * active-tool state, and drawer content (passed as `children`, rendered
 * inside the drawer; the caller switches on `activeTool` itself).
 */
export default function SessionDock({
  tools, activeTool, onSelectTool,
  open, onToggleOpen,
  collapsedIcon: CollapsedIcon,
  drawerWidth = 320,
  children,
}) {
  return (
    <>
      <style>{`
        @keyframes sdockSlideIn { from { transform: translateX(24px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes sdockFadeUp  { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes sdockPulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes sdockSpin    { to { transform: rotate(360deg); } }
        .sdock-line { animation: sdockFadeUp 0.25s ease both; }
        .sdock-spin { animation: sdockSpin 0.8s linear infinite; }
        .sdock-scroll::-webkit-scrollbar { width: 6px; }
        .sdock-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
        .sdock-btn:hover { background: rgba(255,255,255,0.09) !important; }
      `}</style>

      {!open && (
        <button onClick={() => onToggleOpen(true)} title="Show tools"
          style={{ position: "fixed", top: "50%", right: 0, transform: "translateY(-50%)", zIndex: 1000,
            background: "linear-gradient(135deg,#00C8FF,#7C5CFF)", border: "none", borderRadius: "10px 0 0 10px",
            padding: "14px 8px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
            boxShadow: "-4px 0 20px rgba(0,0,0,0.3)" }}>
          <CollapsedIcon size={16} color="#fff" />
          <ChevronLeft size={14} color="#fff" />
        </button>
      )}

      {open && (
        <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999, display: "flex", fontFamily: "Outfit,sans-serif" }}>
          {activeTool && (
            <div style={{ width: drawerWidth, background: "rgba(10,20,35,0.92)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
              borderLeft: "1px solid rgba(0,200,255,0.15)", display: "flex", flexDirection: "column", animation: "sdockSlideIn 0.25s ease both" }}>
              {children}
            </div>
          )}

          <div style={{ width: 56, background: "rgba(10,20,35,0.96)", borderLeft: "1px solid rgba(255,255,255,0.08)",
            display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 0", gap: 10 }}>
            {tools.map(({ key, icon: Icon, label, badge, standalone, onClick }) => {
              // A "standalone" tool (e.g. one that opens a full Dialog instead of the
              // drawer — a code editor needs real width, not a 320px panel) fires its
              // own onClick instead of driving the shared activeTool/drawer state.
              const isActive = !standalone && activeTool === key;
              return (
                <button key={key} title={label} onClick={() => (standalone ? onClick?.() : onSelectTool(key))} className="sdock-btn"
                  style={{ position: "relative", width: 40, height: 40, borderRadius: 11, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: isActive ? "linear-gradient(135deg,#00C8FF,#7C5CFF)" : "rgba(255,255,255,0.05)",
                    transition: "background 0.2s" }}>
                  <Icon size={17} color={isActive ? "#fff" : "#7A93B4"} />
                  {badge != null && (
                    <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, padding: "0 3px",
                      borderRadius: 8, background: "#FF4D6D", color: "#fff",
                      fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
                      border: "2px solid rgba(10,20,35,1)" }}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
            <div style={{ flex: 1 }} />
            <button onClick={() => onToggleOpen(false)} title="Hide" style={{ background: "none", border: "none", cursor: "pointer", color: "#7A93B4" }}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
